import { DocumentNode } from 'graphql';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import {
  ApolloClient,
  ApolloError,
  useApolloClient,
  useQuery,
} from '@apollo/client';
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import papaparse from 'papaparse';
import { useDebouncedCallback } from 'use-debounce';
import { downloadFile } from '../helpers/download';
import useNotify from './useNotify';
import { Subscription } from 'zen-observable-ts';

const pathObjCache = new Map();

const reOpenBracket = /\[/g;
const reCloseBracket = /\]/g;

export type IdArrayInput<T extends string | number = string> =
  | T[]
  | readonly T[];

function flattenDeep(arr: any[], newArr = []) {
  if (!Array.isArray(arr)) {
    newArr.push(arr);
  } else {
    for (let i = 0; i < arr.length; i += 1) {
      flattenDeep(arr[i], newArr);
    }
  }
  return newArr;
}

function makePathArray(obj: any) {
  return (
    flattenDeep(obj)
      // remove all periods in parts
      .map((d) => String(d).replace('.', '_'))
      // join parts using period
      .join('.')
      // replace brackets with periods
      .replace(reOpenBracket, '.')
      .replace(reCloseBracket, '')
      // split it back out on periods
      .split('.')
  );
}

export function getBy(obj: any, path: string[], def?: any) {
  if (!path) {
    return obj;
  }
  const cacheKey = typeof path === 'function' ? path : JSON.stringify(path);

  const pathObj =
    pathObjCache.get(cacheKey) ||
    (() => {
      const pathObj = makePathArray(path);
      pathObjCache.set(cacheKey, pathObj);
      return pathObj;
    })();

  let val;

  try {
    val = pathObj.reduce(
      (cursor: any, pathPart: string) => cursor[pathPart],
      obj
    );
  } catch (e) {
    // continue regardless of error
  }
  return typeof val !== 'undefined' ? val : def;
}

type HasAccessor =
  | {
      accessor: string | number;
      stringAccessor?: undefined;
    }
  | {
      accessor: (row: any) => string | number;
      stringAccessor?: undefined;
    }
  | {
      accessor: (row: any) => JSX.Element | '' | undefined;
      stringAccessor: (row: any) => string | number;
    };

export type ColumnIdentity = (
  | {
      id: string;
    }
  | {
      id?: undefined;
      accessor: string;
    }
) &
  HasAccessor;

export type Column = {
  header?: any;
  width?: number | string;
  fillRemainingWidth?: boolean;
  sortField?: string;
  sortable?: boolean;
  minWidth?: number;
  maxWidth?: number;
} & ColumnIdentity &
  HasAccessor;

export function assignColumnAccessor(
  column: Column
): Column & { accessor: (row: any) => string } {
  // First check for string accessor
  let { id, accessor } = column;
  const { header } = column;
  if (typeof accessor === 'string') {
    id = id || accessor;
    const accessorPath = accessor.split('.');
    accessor = (row: any) => getBy(row, accessorPath);
  }

  if (!id && typeof header === 'string' && header) {
    id = header as string;
  }

  if (!id) {
    console.error(column);
    throw new Error('A column ID (or string accessor) is required!');
  }

  Object.assign(column, {
    id,
    accessor,
  });

  return column as any;
}

export const getPageIndex = (itemIndex: number, pageSize: number): number => {
  return Math.floor(itemIndex / pageSize);
};

export const getPageIndexes = (
  itemIndexes: readonly number[] | number[],
  pageSize: number
): number[] => {
  const pageIndexesIndex: { [pageIndex: string]: boolean } = {};
  const pageIndexes: number[] = [];

  itemIndexes.forEach((itemIndex) => {
    const pageIndex = Math.floor(itemIndex / pageSize);
    if (pageIndex in pageIndexesIndex) {
      return;
    }
    pageIndexesIndex[pageIndex] = true;
    pageIndexes.push(pageIndex);
  });

  pageIndexes.sort();
  return pageIndexes;
};

export const getPage = <T extends string | number = string>(
  items: readonly T[],
  pageIndex: number,
  pageSize: number
): T[] => {
  const startIndex = pageIndex * pageSize;
  return items.slice(startIndex, startIndex + pageSize);
};

export type ItemResult<T extends {} = {}> = {
  [queryName: string]: T[];
};

export type ItemLoader<
  Item extends { [Id in IdField]: IdValue },
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
> = {
  load: (index: number) => Promise<Item>;
  useItem: (index: number) => Item | undefined;
  get: (index: number) => Item;
  loadIds: (itemIds: IdArrayInput<IdValue>) => Promise<Item[]>;
  getById: (itemId: IdValue) => Item;
  has: (index: number) => boolean;
  loadAll: () => Promise<Item[]>;
  downloadIds: (
    itemIds: IdArrayInput<IdValue>,
    columns: Column[],
    filename: string
  ) => Promise<void>;
  downloadAll: (columns: Column[], filename: string) => Promise<void>;
  readonly queryName: string;
};

class Loader<
  Item extends { [Id in IdField]: IdValue },
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
> implements ItemLoader<Item, IdField, IdValue>
{
  private pagePromises: Array<Array<Promise<void>>> = [];
  private loadedItemsById: Record<IdValue, Item> = {} as Record<IdValue, Item>;
  private loadedItems: Item[] = [];
  private itemIndexes: { [itemId: string]: number } = {};

  constructor(
    public readonly queryName: string,
    private hydrateQuery:
      | DocumentNode
      | TypedDocumentNode<ItemResult<Item>, any>,
    private hydrateVariables: (ids: IdValue[]) => any,
    private pageSize: number,
    private client: ApolloClient<any>,
    private itemIds: IdArrayInput<IdValue>,
    private idField: IdField = 'id' as any
  ) {
    itemIds.forEach((itemId, index) => {
      this.itemIndexes[itemId] = index;
    });
  }

  async load(itemIndex: number) {
    if (this.loadedItems[itemIndex]) {
      return this.loadedItems[itemIndex];
    }

    const pageIndex = getPageIndex(itemIndex, this.pageSize);
    await this.loadPage(pageIndex);
    return this.loadedItems[itemIndex];
  }

  async loadIds(itemIds: IdArrayInput<IdValue>) {
    const itemIndexes: number[] = (itemIds as any).map(
      (itemId: string) => this.itemIndexes[itemId]
    );

    const missingItemIds = itemIds.filter(
      (itemId) => !(itemId in this.loadedItemsById)
    );
    const missingItemIndexes: number[] = missingItemIds.map(
      (itemId: string | number) => this.itemIndexes[itemId]
    );
    const missingPageIndexes = getPageIndexes(
      missingItemIndexes,
      this.pageSize
    );

    for (const pageIndex of missingPageIndexes) {
      await this.loadPage(pageIndex);
    }

    return itemIndexes.map((itemIndex) => this.loadedItems[itemIndex]);
  }

  downloadItems(items: Item[], columns: Column[], filename: string) {
    const csv = papaparse.unparse({
      fields: columns.map((column) => column.header),
      data: items.map((item) =>
        columns.map((column) =>
          (column.stringAccessor || (column.accessor as (row: any) => string))(
            item
          )
        )
      ),
    });
    downloadFile(filename, csv);
  }

  async downloadIds(
    itemIds: IdArrayInput<IdValue>,
    columns: Column[],
    filename: string
  ) {
    if (
      this.itemIds.length > 100 &&
      itemIds.length > this.itemIds.length * 0.75
    ) {
      await this.loadAll();
    }
    this.downloadItems(await this.loadIds(itemIds), columns, filename);
  }

  async downloadAll(columns: Column[], filename: string) {
    this.downloadItems(await this.loadAll(), columns, filename);
  }

  useItem(itemIndex: number): Item | undefined {
    const [item, setItem] = useState(undefined as Item | undefined);
    const items = this.usePage(getPageIndex(itemIndex, this.pageSize));
    useEffect(() => {
      if (this.loadedItems[itemIndex]) {
        setItem(this.loadedItems[itemIndex]);
      }
    }, [items]);
    return item;
  }

  async loadAll() {
    const lastPageIndex = getPageIndex(this.itemIds.length, 1000);
    for (let pageIndex = 0; pageIndex <= lastPageIndex; ++pageIndex) {
      await this.loadPage(pageIndex, 1000);
    }
    return this.loadedItems;
  }

  has(index: number) {
    return index in this.loadedItems;
  }

  get(index: number) {
    return this.loadedItems[index];
  }

  getById(itemId: IdValue) {
    return this.loadedItemsById[itemId];
  }

  private usePage(pageIndex: number, pageSize = this.pageSize): Item[] {
    const [items, setItems] = useState([] as Item[]);
    const itemIds = getPage(this.itemIds, pageIndex, pageSize);

    const { loading, data } = useQuery(this.hydrateQuery, {
      variables: this.hydrateVariables(itemIds),
    });

    useEffect(() => {
      if (!loading && data) {
        const newItems = data[this.queryName];
        this.indexResult(newItems);
        setItems(newItems);
      }
    }, [loading, data]);

    return items;
  }

  private indexResult(items: Item[]): void {
    items.forEach((item: Item) => {
      const itemIndex = this.itemIndexes[item[this.idField]];
      this.loadedItemsById[item[this.idField]] = item;
      this.loadedItems[itemIndex] = item;
    });
  }

  private loadPage(pageIndex: number, pageSize = this.pageSize): Promise<void> {
    if (!this.pagePromises[pageSize]) {
      this.pagePromises[pageSize] = [];
    }

    if (!this.pagePromises[pageSize][pageIndex]) {
      console.log(`Loading page ${pageIndex + 1} of ${this.queryName}...`);
      const itemIds = getPage(this.itemIds, pageIndex, pageSize);
      const missingItemIds = itemIds.filter(
        (itemId) => !(itemId in this.loadedItemsById)
      );

      this.pagePromises[pageSize][pageIndex] = this.client
        .query({
          query: this.hydrateQuery,
          variables: this.hydrateVariables(missingItemIds),
        })
        .then((result) => {
          this.indexResult(result.data[this.queryName]);
        });
    }
    return this.pagePromises[pageSize][pageIndex];
  }
}

export default function useTablePages<
  Item extends { [Id in IdField]: IdValue },
  IdField extends string = 'id',
  IdItem extends { [Id in IdField]: Item[IdField] } = {
    [Id in IdField]: Item[IdField];
  },
  IdValue extends string | number = Item[IdField],
>(
  queryName: string,
  idsQuery: DocumentNode | TypedDocumentNode<ItemResult, IdItem>,
  idsVariables: any,
  hydrateQuery: DocumentNode | TypedDocumentNode<ItemResult<Item>>,
  hydrateVariables: (ids: IdValue[]) => any,
  idField: IdField = 'id' as IdField,
  pageSize = 50,
  onError?: (error: Error | ApolloError) => void
) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(undefined as Error | undefined);
  const [idItems, setIdItems] = useState([] as readonly IdItem[]);
  const [itemIds, setItemIds] = useState([] as IdValue[]);
  const [indexedItemIds, setIndexedItemIds] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedIds, setSelectedIds] = useState<IdArrayInput<IdValue>>([]);
  const client = useApolloClient();
  const { showError } = useNotify();
  const queryRef = useRef<Subscription>();

  useEffect(() => {
    return () => {
      if (queryRef.current) {
        queryRef.current.unsubscribe();
      }
    };
  }, []);

  const runQuery = () => {
    if (queryRef.current) {
      queryRef.current.unsubscribe();
    }

    const watched = client.watchQuery<Record<string, IdItem[]>>({
      query: idsQuery,
      variables: idsVariables,
      fetchPolicy: 'no-cache',
    });

    queryRef.current = watched.subscribe({
      next(result) {
        setLoading(false);
        const newIndexedItemIds: Record<IdValue, boolean> = {} as Record<
          IdValue,
          boolean
        >;
        const data = result.data[queryName];
        if (JSON.stringify(idItems) !== JSON.stringify(data)) {
          setIdItems(data);
        } else {
          return;
        }

        const newItemIds = data.map((item) => {
          const id = item[idField];
          newIndexedItemIds[id] = true;
          return id;
        });
        setItemIds(newItemIds);
        setSelectedIds(
          selectedIds.filter((selectedId) => newIndexedItemIds[selectedId])
        );
        setIndexedItemIds(newIndexedItemIds);
      },
      start(): any {
        setLoading(true);
      },
      complete() {
        setLoading(false);
      },
      error(e) {
        setLoading(false);

        if (onError) {
          onError(e);
        } else {
          showError('Error loading table data', e);
        }
        setError(e);
      },
    });
  };

  const debouncedQuery = useDebouncedCallback(runQuery, 400, {
    leading: true,
    maxWait: 1000,
  });

  useEffect(debouncedQuery, [
    queryName,
    idsQuery,
    JSON.stringify(idsVariables),
  ]);

  const setValidSelectedIds = useCallback(
    (selectedIds: IdArrayInput<IdValue>) => {
      setSelectedIds(
        selectedIds.filter((selectedId) => indexedItemIds[selectedId])
      );
    },
    [setSelectedIds, indexedItemIds]
  );

  const itemLoader: ItemLoader<Item, IdField> = useMemo(() => {
    if (itemIds.length > 0) {
      console.log(`Creating ${queryName} loader...`);
    }

    return new Loader(
      queryName,
      hydrateQuery,
      hydrateVariables,
      pageSize,
      client,
      itemIds,
      idField
    );
  }, [queryName, hydrateQuery, pageSize, itemIds, idField]);

  return {
    loading,
    error,
    itemIds,
    idItems: idItems,
    selectedIds,
    setSelectedIds: setValidSelectedIds,
    itemLoader,
    refetch: runQuery,
  };
}
